import { PIF_LOOKUP_RESULT, PIF_SUB_FLOW } from "../../../data/constants";
import { PostIfIdentityLookupResultDto } from "../../../types/pifDto";
import { PifCandidate, PifLookupResult } from "./types";

/** Fallback when the API blocks a person without saying why — we never invent a reason. */
const BLOCKED_FALLBACK =
  "This person can't be hired through this form. Please reach out to HR with any questions.";

/**
 * Reduces an identity-lookup response to the three states the start screen renders.
 *
 * Contact details are only carried over for the rehire (`inactive`) sub-flow — the API
 * omits them for an active employee, who maintains them in self-service. `maskedSsn` is
 * read but never carried into the candidate: it is fully redacted (`***-**-****`, no
 * digits at all), and the Epic requires the SSN is never shown, "not even the last four"
 * (PR review, Pooja Pujara — this comment previously said "last four digits", which is
 * wrong; behavior was already correct, only the comment was stale).
 */
export const mapIdentityLookupResult = (dto: PostIfIdentityLookupResultDto): PifLookupResult => {
  if (dto.result === PIF_LOOKUP_RESULT.BLOCKED) {
    // employeeId is carried through for the card's "Contact HR" email (Bug 82168). It is the only
    // identifier that may leave the browser for that request - never the typed lookup value, which
    // may be an SSN.
    return { kind: "blocked", message: dto.message || BLOCKED_FALLBACK, employeeId: dto.employeeId ?? null };
  }

  if (dto.result !== PIF_LOOKUP_RESULT.EXISTING) {
    return { kind: "new-applicant" };
  }

  // An "existing" result without a sub-flow would be an API contract break; treat the
  // person as previously employed, which is the flow that asks the most questions
  // rather than the one that locks fields down.
  const flow = dto.subFlow === PIF_SUB_FLOW.ACTIVE ? PIF_SUB_FLOW.ACTIVE : PIF_SUB_FLOW.INACTIVE;
  const isRehire = flow === PIF_SUB_FLOW.INACTIVE;

  const candidate: PifCandidate = {
    status: flow,
    employeeId: dto.employeeId ?? undefined,
    first: dto.firstName ?? "",
    middle: dto.middleName ?? "",
    last: dto.lastName ?? "",
    dob: dto.dob ? dto.dob.slice(0, 10) : "",
    addr1: isRehire ? (dto.address1 ?? "") : "",
    city: isRehire ? (dto.city ?? "") : "",
    state: isRehire ? (dto.state ?? "") : "",
    zip: isRehire ? (dto.zip ?? "") : "",
    home: isRehire ? (dto.phone ?? "") : "",
    cell: isRehire ? (dto.cellPhone ?? "") : "",
    email: isRehire ? (dto.email ?? "") : "",
  };

  return {
    kind: "match",
    flow,
    candidate,
    existingDraftPostIfId: dto.existingDraftPostIfId ?? null,
    existingDraftRefNo: dto.existingDraftRefNo ?? null,
  };
};
